<template>
  <div>
    <v-card
      :id="`id_${analysisRequest.id}`"
      class="bloc-card"
      @mouseover="showMore = true"
      @mouseleave="showMore = true"
    >
      <v-toolbar
        dense
        color="#4E719F"
        dark
      >
        <v-toolbar-title>
          {{ analysisRequest.requestNumber }}
        </v-toolbar-title>

        <v-spacer />

        <v-toolbar-items>
          <v-btn
            icon
            @click="onClickUpdateAnalysisRequest"
          >
            <v-icon>
              edit
            </v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-container>
        <v-row
          align="center"
          justify="center"
        >
          <v-col cols="6">
            <VTextField
              :value="$t(`sampleTypes.${lowerCaseFirstletter(analysisRequest.type)}`)"
              :label="$t('forms.labels.type')"
              :rules="{ required: false }"
              readonly
            />
          </v-col>
        </v-row>

        <v-row
          align="start"
          justify="center"
        >
          <v-col cols="6">
            <v-container>
              <v-row
                align="start"
                justify="start"
                class="mb-2"
              >
                {{ $t('forms.labels.analyses') }}
              </v-row>
              <template v-if="!showAll">
                <v-row
                  align="start"
                  justify="start"
                  class="mb-2"
                >
                  <span
                    class="showAll"
                    @click="showAll = !showAll"
                  >
                    {{ $t('forms.labels.showAll') }}
                  </span>
                </v-row>
              </template>
              <template v-if="showAll">
                <v-row
                  align="start"
                  justify="start"
                  class="mb-2"
                >
                  <span
                    class="showAll"
                    @click="showAll = !showAll"
                  >
                    {{ $t('forms.labels.showLess') }}
                  </span>
                </v-row>
                <v-row
                  v-for="analysisType in analysisTypes"
                  :key="analysisType.ref"
                  align="start"
                  justify="start"
                >
                  <span
                    :title="analysisType.name"
                    class="sample-card-item"
                  >
                    {{ analysisType.name }}
                  </span>
                </v-row>
              </template>
            </v-container>
          </v-col>

          <v-col cols="6">
            <v-container>
              <v-row
                align="start"
                justify="start"
                class="mb-2"
              >
                {{ $t('forms.labels.samples') }}
              </v-row>
              <v-row
                v-for="sample in samples"
                :key="sample.id"
                align="start"
                justify="start"
              >
                <span
                  :title="sample.identification"
                  class="sample-card-item"
                >
                  {{ sample.identification }}
                </span>
              </v-row>
            </v-container>
          </v-col>
        </v-row>

        <v-row
          align="center"
          justify="center"
        >
          <v-btn
            v-if="!showMore || analysisRequest.analysisTypes.length <= 1 || analysisRequest.samples.length <= 1"
            :disabled="analysisRequest.analysisTypes.length <= 1 || analysisRequest.samples.length <= 1"
            text
          >
            ...
          </v-btn>
        </v-row>

        <v-row
          align="end"
          justify="end"
          class="mr-1"
        >
          <v-btn
            color="secondary"
            class="square-btn"
            @click="onClickConfirmRemove"
          >
            <v-icon>
              mdi-delete-outline
            </v-icon>
          </v-btn>
        </v-row>
      </v-container>
    </v-card>

    <WizardModal ref="wizardModal" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import SampleForm from '@/components/forms/sample/SampleForm';
import WizardModal from '@/components/shared/forms-controls/WizardModal';

export default {
    components: {
        VTextField,
        WizardModal,
    },
    props: {
        analysisRequest: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            showMore: true,
            showAll: false,
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'workOrder',
        ]),
        lowerCaseFirstletter() {
            return value => this.$options.filters.lowercase(value.charAt(0)) + value.slice(1);
        },
        samples() {
            const { samples } = this.analysisRequest;
            return samples.map(sample => ({
                id: sample.id,
                identification: sample.identification,
            })).slice(0, this.showMore ? samples.length : 1);
        },
        analysisTypes() {
            const { analysisTypes } = this.analysisRequest;
            return analysisTypes.map(({ ref, name }) => ({
                ref,
                name,
            })).slice(0, this.showMore ? analysisTypes.length : 1);
        },
    },
    methods: {
        ...mapActions({
            reloadWorkOrderAnalysisRequests: 'getWorkOrderAnalysisRequests',
        }),
        onClickUpdateAnalysisRequest() {
            this.$root.$emit('showModal', {
                showNextPrevious: true,
                title: this.$t('analysisRequests.edit'),
                component: Vue.component('SampleForm', SampleForm),
                payload: {
                    isWorkOrder: true,
                    isEdition: true,
                    sample: {
                        ...this.analysisRequest,
                        client: this.analysisRequest.client.id,
                        site: this.analysisRequest.site.id,
                        component: this.analysisRequest.component.id,
                    },
                },
                onConfirm: requestPayload => this.updateAnalysisRequest(this.analysisRequest.id, requestPayload),
            });
        },
        prepareAnalysisRequest(requestPayload) {
            const payload = {};

            Object.keys(requestPayload).forEach(key => {
                if (requestPayload[key] && requestPayload[key].id) {
                    payload[key] = requestPayload[key].id;
                } else if (requestPayload[key]) {
                    payload[key] = requestPayload[key];
                }
            });

            payload.requestedBy = this.currentMember.id;
            payload.workOrder = this.workOrder.id;

            return payload;
        },
        async updateAnalysisRequest(analysisRequestId, requestPayload) {
            const payload = this.prepareAnalysisRequest(requestPayload);
            payload.samples.forEach((element, index) => {
                payload.samples[index].collectedBy = payload.samples[index].collectedBy.id;
            });

            /* Edit this element for backend */
            payload.selectedAnalysisGroups = payload.selectedAnalysisGroups.map(selectedAnalysisGroup => selectedAnalysisGroup.id);

            const { data } = await this.$rq.updateAnalysisRequest(analysisRequestId, payload);
            this.$toasted.global.toast_success({
                message: this.$t('analysisRequests.updated', { name: data.requestNumber }),
            });
            await this.reloadWorkOrderAnalysisRequests(this.workOrder.id);
        },
        async removeAnalysisRequest(analysisRequest) {
            await this.$rq.removeAnalysisRequest(analysisRequest.id);
            this.$toasted.global.toast_success({
                message: this.$t('analysisRequests.removed', { name: analysisRequest.requestNumber }),
            });
            await this.reloadWorkOrderAnalysisRequests(this.workOrder.id);
        },
        onClickConfirmRemove() {
            this.$root.$emit('showModal', {
                title: this.$t('analysisRequests.remove'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('workOrders.confirmRemoveSample') },
                onConfirm: () => this.removeAnalysisRequest(this.analysisRequest),
                isForm: false,
            });
        },
    },
};
</script>
<style scoped lang="scss">
  .bloc-card {
    min-height: 300px
  }
  .showAll{
    text-decoration: underline;
}
</style>
